/** Voice-template → Generate Audio edge helpers (single active template). */

import type { Edge } from '@xyflow/react'
import { AUDIO_VOICE_TEMPLATE_IN_HANDLE, GENERIC_INPUT_HANDLE } from './dataflow'
import type { FlowNodeData } from './types'

export const VOICE_TEMPLATE_OUTPUT_HANDLE = 'voice-template-out'

export function isVoiceTemplateSourceNode(nodeType: string | undefined): boolean {
  return nodeType === 'generate_voice_template'
}

export function isVoiceTemplateToGenerateAudioEdge(
  edge: Edge,
  nodeTypeById: Map<string, string>,
): boolean {
  // VistoeraTASK-00074 — accepts BOTH the legacy shared
  // `GENERIC_INPUT_HANDLE` (every voice-template edge persisted before
  // this round) and the new dedicated `AUDIO_VOICE_TEMPLATE_IN_HANDLE`
  // (every edge created from now on, via `buildVoiceTemplateEdge`
  // below) — never breaks a pre-existing Canvas's already-persisted
  // edges just because the wire id this round starts writing changed.
  if (
    edge.targetHandle !== GENERIC_INPUT_HANDLE &&
    edge.targetHandle !== AUDIO_VOICE_TEMPLATE_IN_HANDLE &&
    edge.targetHandle != null
  ) {
    return false
  }
  if (edge.sourceHandle && edge.sourceHandle !== VOICE_TEMPLATE_OUTPUT_HANDLE) return false
  return isVoiceTemplateSourceNode(nodeTypeById.get(edge.source))
}

export function voiceTemplateEdgesToTarget(edges: Edge[], targetId: string, nodeTypeById: Map<string, string>): Edge[] {
  return edges.filter((e) => e.target === targetId && isVoiceTemplateToGenerateAudioEdge(e, nodeTypeById))
}

export function stripVoiceTemplateEdgesToTarget(
  edges: Edge[],
  targetId: string,
  nodeTypeById: Map<string, string>,
): Edge[] {
  return edges.filter((e) => !isVoiceTemplateToGenerateAudioEdge(e, nodeTypeById) || e.target !== targetId)
}

export function buildVoiceTemplateEdge(args: {
  id: string
  sourceId: string
  targetId: string
}): Edge {
  return {
    id: args.id,
    source: args.sourceId,
    sourceHandle: VOICE_TEMPLATE_OUTPUT_HANDLE,
    target: args.targetId,
    targetHandle: AUDIO_VOICE_TEMPLATE_IN_HANDLE,
    data: { relationType: 'VOICE_TEMPLATE' },
    className: 'pcv-edge',
  }
}

export function nodeTypeMapFromNodes(nodes: Array<{ id: string; data: unknown }>): Map<string, string> {
  const map = new Map<string, string>()
  for (const n of nodes) {
    map.set(n.id, (n.data as FlowNodeData).nodeType)
  }
  return map
}

export function audioOutputReady(fields: Record<string, unknown> | undefined): boolean {
  return fields?.status === 'COMPLETED' && Boolean(fields?.audioAssetId)
}

export function audioOutputStale(fields: Record<string, unknown> | undefined): boolean {
  return Boolean(fields?.needsRegeneration)
}

export interface ConnectedVoiceTemplateBinding {
  templateId: string
  label: string
  providerVoiceId: string | null
  ready: boolean
  executable: boolean
}

/** VistoeraTASK-00187 §八/§九 — the ONE canonical place that resolves a
 * `generate_voice_template` → `generate_voice` Canvas edge into a
 * structured template binding, reading ONLY already-hydrated in-memory
 * `nodes`/`edges` state (zero network requests — satisfies the
 * NEW_NETWORK_REQUEST_PER_RENDER=0 requirement). `CanvasBoard.tsx` must
 * call this instead of maintaining its own inline copy of this logic, so
 * "手动选择声音模板" and "连线进入声音模板" always resolve through the
 * exact same source-of-truth shape (§十). */
export function buildConnectedVoiceTemplateMap(
  nodes: Array<{ id: string; data: unknown }>,
  edges: Edge[],
  fallbackLabel = '',
): Map<string, ConnectedVoiceTemplateBinding> {
  const nodeTypeById = nodeTypeMapFromNodes(nodes)
  const nodesById = new Map(nodes.map((n) => [n.id, n]))
  const map = new Map<string, ConnectedVoiceTemplateBinding>()
  for (const e of edges) {
    if (!isVoiceTemplateToGenerateAudioEdge(e, nodeTypeById)) continue
    const sourceNode = nodesById.get(e.source)
    const targetNode = nodesById.get(e.target)
    if (!sourceNode || !targetNode) continue
    const sData = sourceNode.data as FlowNodeData
    const tData = targetNode.data as FlowNodeData
    if (sData.nodeType !== 'generate_voice_template' || tData.nodeType !== 'generate_voice') continue
    const templateId = sData.fields?.templateId as string | undefined
    const label = (sData.fields?.templateName as string | undefined) || fallbackLabel
    const executionAvailability = (sData.fields?.executionAvailability as string | undefined) ?? 'UNKNOWN'
    map.set(e.target, {
      templateId: templateId || '',
      label,
      providerVoiceId: (sData.fields?.providerVoiceId as string | null | undefined) ?? null,
      ready: sData.fields?.status === 'COMPLETED' && !!templateId,
      executable: executionAvailability !== 'UNAVAILABLE',
    })
  }
  return map
}

/** VistoeraTASK-00187 §十八 — reject a duplicate quick-create fire for the
 * SAME source node within `cooldownMs` (a rapid double click / duplicate
 * event must never create two nodes + two edges from one user action).
 * Pure predicate so it is unit-testable without mounting `CanvasBoard`;
 * the caller is responsible for actually recording `nowMs` into its own
 * `lastFiredAtBySourceId` map after an allowed call. */
export function shouldAllowVoiceTemplateQuickCreate(
  sourceNodeId: string,
  lastFiredAtBySourceId: Map<string, number>,
  nowMs: number,
  cooldownMs = 800,
): boolean {
  const lastAt = lastFiredAtBySourceId.get(sourceNodeId)
  if (lastAt != null && nowMs - lastAt < cooldownMs) return false
  return true
}
